import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { LoaderSpinner } from "../LoaderSpinner";
import { OverlayGrayBackground } from "../OverlayGrayBackground";
import { initProductFromShipheroRequest, updateProductFromShipheroRequest } from "../../slices/productCostSlice";
import { useDispatch } from "react-redux";
import { arrayHasItems } from "../../helpers/common";

export const AddProductModal = ({
    isOpen,
    onClose,
}) => {
    const dispatch = useDispatch();
    const [productSku, setProductSku] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [confirmData, setConfirmData] = useState({
        text: "",
        state: null,
        productData: null,
        productNotFound: false
    });

    useEffect(() => {
        if (!isOpen) {
            setProductSku("");
            setConfirmData({ text: "", state: null, productData: null, productNotFound: false });
            setIsLoading(false);
        }
    }, [isOpen]);

    const getImageByFirstPosition = (images) => {
        if (!arrayHasItems(images)) return null;
        const hasPosition = images.some((img) => typeof img?.position === 'number');
        if (!hasPosition) {
            return images[0]?.src ?? null;
        }
        const sorted = [...images].sort((a, b) => {
            const posA = typeof a?.position === 'number' ? a.position : Infinity;
            const posB = typeof b?.position === 'number' ? b.position : Infinity;
            return posA - posB;
        });
        return sorted[0]?.src ?? null;
    }

    const handleActivateAndUpdateProduct = async () => {
        const stateToApply = confirmData.state;
        if (!stateToApply) {
            setConfirmData({ text: "", state: null, productData: null, productNotFound: false });
            return;
        }
        setIsLoading(true);
        try {
            await dispatch(updateProductFromShipheroRequest({ sku: productSku, state: stateToApply, productData: confirmData.productData })).unwrap();
            setConfirmData({ text: "", state: null, productData: null, productNotFound: false });
            setProductSku("");
            onClose();
        } catch (e) {
            console.error('Failed to update product.', e);
            toast.error(e.message || 'Failed to update product.', { position: "bottom-right" });
            setConfirmData({ text: "", state: null, productData: null, productNotFound: false });
        } finally {
            setIsLoading(false);
        }
    }

    const handleAddProduct = async () => {
        if (!productSku) {
            toast.error("Product SKU is required.", { position: "bottom-right" });
            return;
        }
        setIsLoading(true);
        try {
            const res = await dispatch(initProductFromShipheroRequest({ productSku })).unwrap();
            if (res.success) {
                if (res.productWasCreated) {
                    setConfirmData({
                        text: "Product in Shiphero.",
                        state: "update",
                        productData: res.productData,
                        productNotFound: true
                    });
                    setIsLoading(false);
                    return;
                }
                if (res.productAlreadyExists && res.productIsDeleted) {
                    setConfirmData({
                        text: "Product in Eden.",
                        state: "activateAndUpdate",
                        productData: res.productData,
                        productNotFound: false
                    });
                    setIsLoading(false);
                    return;
                }
                if (res.productAlreadyExists && !res.productIsDeleted) {
                    setConfirmData({
                        text: "Product in Eden.",
                        state: "update",
                        productData: res.productData,
                        productNotFound: false
                    });
                    setIsLoading(false);
                    return;
                }
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Failed to add product.', error);
        }
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={onClose}
                ></div> 
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:align-middle"
                    onClick={(e) => e.stopPropagation()}
                >
                    {isLoading && !confirmData.isOpen && (
                        <>
                            <OverlayGrayBackground />
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                                <LoaderSpinner colorSpinner="black" height="70" width="70" border="4" />
                            </div>
                        </>
                    )}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Import Product</h3>
                        <p className="text-sm text-gray-500 mb-2">Import a new product from Shiphero</p>
                        <div className="sm:flex sm:items-start">
                            <div className="mt-2 w-full text-center sm:mt-0 sm:text-left">
                                <div className="border-b border-gray-200 mb-3"></div>
                                <input
                                    type="text"
                                    value={productSku}
                                    onChange={(e) => {
                                        setProductSku(e.target.value);
                                        setConfirmData({ text: "", state: null, productData: null, productNotFound: false });
                                        setIsLoading(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (!isLoading) {
                                                handleAddProduct();
                                            }
                                        }
                                    }}
                                    placeholder="Product SKU"
                                    className="w-full p-2 border text-sm border-gray-300 rounded-md"
                                />
                            </div>
                        </div>
                        {confirmData?.productData && (
                            <div className="flex flex-col gap-2 mt-4">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50 border border-gray-200 border-b-0">
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-[100px]">Image</th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-[100px]">Product Name/SKU</th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-[100px]">Barcode</th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-[100px]">Eden Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="border border-gray-200 bg-white">
                                        <tr className="border-b border-gray-200 last:border-b-0 cursor-pointer bg-white">
                                            <td className="py-2 px-4 text-sm text-gray-600">
                                                <div className="w-12 h-12 relative rounded-md overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                                                    <img
                                                        src={getImageByFirstPosition(confirmData.productData?.images) || 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image'}
                                                        alt={confirmData.productData?.name || "N/A"}
                                                        className="max-w-full max-h-full object-contain"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image';
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-sm font-medium">
                                                {confirmData.productData?.name || ""}
                                                <div className="text-xs text-gray-500 mt-1">
                                                    SKU: {confirmData.productData?.sku || ""}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-sm whitespace-nowrap">
                                                {confirmData.productData?.barcode || "N/A"	}
                                            </td>
                                            <td className="py-3 px-4 text-sm whitespace-nowrap">
                                                {confirmData?.productNotFound ? "N/A" : (confirmData?.state === "activateAndUpdate" ? "DELETED" : "ACTIVE")}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="py-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!isLoading) {
                                        if (confirmData?.productData) {
                                            handleActivateAndUpdateProduct();
                                        } else {
                                            handleAddProduct();
                                        }
                                    }
                                }}   
                                disabled={isLoading}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                            >
                                Import
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}